import React from "react";
import {TimeSpan} from "@sparkware/app-utils";
import {ServiceBase} from "../ServiceBase";
import {observable, action} from "mobx";
import {IModalDialogHandler} from "../dialog/dialog.service.interface";
import {CGPEventSubscription} from "../sdk/cgp-sdk.interface";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {SessionCooldownPopupComponent} from "../../components/session-cooldown-popup/session-cooldown-popup.component";
import {ISessionCooldownService, ISessionCooldownDataToken} from "./session-cooldown.service.interface";

function DefaultCooldownDataToken(): ISessionCooldownDataToken {
  return {
    endDateMs: 0
  };
}

export const SESSION_COOLDOWN_STORAGE_KEY: string = "orbit-session-cooldown";

export class SessionColldownService extends ServiceBase implements ISessionCooldownService {

  private _sessionCooldownConfig =  this.services.regulation.getSessionCooldownConfig();
  private _cooldownData: ISessionCooldownDataToken = DefaultCooldownDataToken();

  @observable
  private _cooldownCountdown: TimeSpan = TimeSpan.fromMilliseconds(0);

  private _dialogHandler: IModalDialogHandler = null;
  private _timerUpdateEventHandler: CGPEventSubscription = null;
  private _realityCheckOpenedEventHandler: CGPEventSubscription = null;
  private _realityCheckClosedEventHandler: CGPEventSubscription = null;

  constructor(services: IServiceFactoryExtended) {
    super(services);

    if (this._sessionCooldownConfig.featureEnabled) {
      this.reaction(
        () => this.services.user.isAuthorized,
        isAuthorized => {
          isAuthorized ? this._onUserLogin() : this._onUserLogout();
        },
        {
          fireImmediately: true
        }
      );
    }
  }

  private _onUserLogin() {

    this._checkForValidStorageCooldownData();

    if (!this._timerUpdateEventHandler) {
      this._timerUpdateEventHandler = this.services.sdk.events.onSessionTimerUpdate(() => {
        this._handleTimerUpdate();
      });
    }

    if (!this._realityCheckClosedEventHandler) {
      this._realityCheckClosedEventHandler = this.services.sdk.events.onRealityCheckClosed(() => {
        this._showCooldownDialog();
      });
    }

    if (!this._realityCheckOpenedEventHandler) {
      this.services.sdk.events.onRealityCheckOpened(() => {
        if (this._cooldownData.endDateMs === 0) {
          this._startSessionCooldown();
        }
        this._closeCooldownDialog();
      });
    }
  }

  private _onUserLogout() {

    if (this._timerUpdateEventHandler) {
      this._timerUpdateEventHandler.off();
      this._timerUpdateEventHandler = null;
    }

    if (this._realityCheckClosedEventHandler) {
      this._realityCheckClosedEventHandler.off();
      this._realityCheckClosedEventHandler = null;
    }

    this._closeCooldownDialog();
    this._disposeStorageCooldownData();
  }

  private _startSessionCooldown() {
    this._writeStorageCooldownData();
    this._setCooldownCountdownValue(this._sessionCooldownConfig.cooldownTime.totalMilliseconds);
  }

  private _endSessionCooldown() {
    this._closeCooldownDialog();
    this._resetCooldownCountdown();
    this._disposeStorageCooldownData();
  }

  private _showCooldownDialog() {

    if (this._dialogHandler) return;
    if (this._cooldownData.endDateMs < this.services.time.now()) return;

    this.services.dialog.showModal((dialogHandler) => {
      this._dialogHandler = dialogHandler;
      return <SessionCooldownPopupComponent countdown={this._cooldownCountdown} />
    }, {
      title: this.services.translation.translate("SESSION_COOLDOWN_POPUP_TITLE"),
      isHighPriority: true,
      hideCloseButton: true,
      shouldBeClosedOnlyByUser: true
    });

    this.services.analytics.sendAnalytics("SESSION_COOLDOWN_POPUP", "SHOW_POPUP");
  }

  private _closeCooldownDialog() {
    if (this._dialogHandler) {
      this._dialogHandler.close();
      this._dialogHandler = null;
    }
  }

  private _checkForValidStorageCooldownData() {

    this._loadStorageCooldownData();

    const currentTimeMs = this.services.time.now();

    if (this._cooldownData.endDateMs > currentTimeMs) {
      this._setCooldownCountdownValue(this._cooldownData.endDateMs - currentTimeMs);
      this._showCooldownDialog();
    } else {
      this._disposeStorageCooldownData();
    }
  }

  private _loadStorageCooldownData() {

    const cooldownData = this.services.localStorage.getJson(SESSION_COOLDOWN_STORAGE_KEY);

    if (cooldownData) {
      try {
        this._cooldownData = cooldownData;
      } catch (err) {
        this._cooldownData = DefaultCooldownDataToken();
        this.services.logger.error("Parsing session cooldown data from local storage failed:", err);
      }
    }
  }

  private _writeStorageCooldownData() {

    this._cooldownData = {
      endDateMs: this.services.time.now() + this._sessionCooldownConfig.cooldownTime.totalMilliseconds
    };

    this.services.localStorage.setJson(SESSION_COOLDOWN_STORAGE_KEY, this._cooldownData);
  }

  private _disposeStorageCooldownData() {
    this._cooldownData = DefaultCooldownDataToken();
    this.services.localStorage.removeItem(SESSION_COOLDOWN_STORAGE_KEY);
  }

  @action
  private _setCooldownCountdownValue(timeMs: number) {
    this._cooldownCountdown = TimeSpan.fromMilliseconds(timeMs);
  }

  @action
  private _resetCooldownCountdown() {
    this._cooldownCountdown = TimeSpan.fromMilliseconds(0);
  }

  @action
  private _decreaseCooldownCountdown() {

    this._cooldownCountdown = this._cooldownCountdown.subtract(TimeSpan.fromSeconds(1));

    if (this._cooldownCountdown.totalMilliseconds < 1000) {
      this._endSessionCooldown();
    }
  }

  private _handleTimerUpdate() {
    if (this._cooldownData.endDateMs > this.services.time.now()) {
      this._decreaseCooldownCountdown();
    }
  }
}
